import { IconAllDone } from '@consta/icons/IconAllDone'
import { Button } from '@consta/uikit/Button'
import { useGlobalKeys } from '@consta/uikit/useGlobalKeys'
import type { ClassNameProp } from '@library/types'
import classNames from 'classnames'
import { isDefined } from 'common/helpers'
import type { FormEventHandler, MouseEventHandler, PropsWithChildren } from 'react'

import { Card, CenteredLoader, Delimiter } from '../../../../components'
import { useActionContext } from '../../context'
import css from './Settings.css'

const cn = classNames.bind(css)

type SettingsProps<T> = ClassNameProp & {
  title: string
  applyDisabled?: boolean
  calcIsInProgress?: boolean
  onConfirm: (objects: T[]) => void
  onApply?: (objects: T[]) => void
  onBack?: VoidFunction
}

export const ActionSettings = <T,>({
  title,
  children,
  applyDisabled,
  className,
  calcIsInProgress,
  onConfirm,
  onApply,
  onBack,
}: PropsWithChildren<SettingsProps<T>>) => {
  const { unselect, objects, onResetAction } = useActionContext<T>()

  const handleConfirm: FormEventHandler<HTMLFormElement> = event => {
    event.preventDefault()
    onConfirm(objects)
    onResetAction()
    unselect()
  }

  const handleApply: MouseEventHandler = event => {
    event.preventDefault()
    if (isDefined(onApply)) {
      onApply(objects)
    }
  }

  const handleBack = () => {
    onResetAction()
    unselect()
    onBack?.()
  }

  useGlobalKeys({ Escape: handleBack })

  const applyButton = !calcIsInProgress ? (
    <Button
      size="xs"
      disabled={applyDisabled}
      onlyIcon={true}
      iconLeft={IconAllDone}
      view="ghost"
      onClick={handleApply}
    />
  ) : (
    <CenteredLoader size="xs" className={css.loader} />
  )

  return (
    <form className={cn(css.settings, className)} onSubmit={handleConfirm}>
      <Card.Header
        className={css.settingHeader}
        titleClassName={cn(css.headerTitle, { [css.withApplyButton]: isDefined(onApply) })}
        title={title}
        handleBack={handleBack}>
        <div className={css.headerRightSide}>
          {isDefined(onApply) && applyButton}
          <Button type="submit" label="Готово" size="xs" view="primary" disabled={applyDisabled} />
        </div>
      </Card.Header>
      <Delimiter position="horizontal" size="100%" />
      <div className={css.settingsBlock}>{children}</div>
    </form>
  )
}
